import { CampaignIsArchived, CampaignNotAuthorized } from '@/components'
import { isCampaignArchived, isNotAuthorized } from '@/errors'
import { campaignGuard } from '@/guards'
import {
	useCampaignData,
	useCampaignPoller,
	useDirectImageTools,
	useIsJobillan,
	useNewCampaignComplete,
	useNewCampaignDetails,
	useNewCampaignReview,
	useNewEditorRegenerate,
	useNewExportConfig,
	useNewExportOptions,
	useNewFeedEditor,
	useNewFormEditor,
	useNewLandingPageEditor,
	useNewStoryEditor,
	useNewThankYouScreenEditor,
	useSupplyBrandingLibrary,
} from '@/hooks'
import { queryClient } from '@/main.tsx'
import {
	EditModeEditor,
	EditorEditSimulatorsRef,
	logger,
	showCanvasToolbarAtom,
	useEditorRefresh,
	useEditorTracking,
} from '@/ui'
import { campaignsShowQueryKey, useCampaignsShow } from '@jobilla/nexus-sdk'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'

export const Route = createFileRoute(
	'/_auth/company/$companyId/campaign/$campaignId',
)({
	component: RouteComponent,
	beforeLoad: async ({ params, context }) => {
		// Recruiters (no campaign:update) can view but not edit — send them to the read view.
		if (!context.auth.permissions.includes('campaign:update')) {
			throw redirect({ to: '/company/$companyId/$campaignId', params })
		}

		await campaignGuard(params, ['archived', 'draft'])
	},
	errorComponent: ({ error }) => {
		if (isNotAuthorized(error)) {
			return <CampaignNotAuthorized {...error.data} />
		}

		if (isCampaignArchived(error)) {
			return <CampaignIsArchived {...error.data} />
		}
	},
})

function RouteComponent() {
	const isJobillan = useIsJobillan()
	const setShowCanvasToolbar = useSetAtom(showCanvasToolbarAtom)
	useEffect(() => {
		setShowCanvasToolbar(isJobillan)
		return () => setShowCanvasToolbar(false)
	}, [isJobillan, setShowCanvasToolbar])

	const { store: brandStore } = useSupplyBrandingLibrary({ enableSleeping: false })
	const params = Route.useParams()

	useEditorTracking(params.campaignId)

	useEffect(() => {
		logger.setGroup('campaign', params.campaignId)
		return () => logger.setGroup('campaign', null)
	}, [params.campaignId])

	const navigate = useNavigate()

	const rawCampaign = useCampaignsShow(params.campaignId)
	const campaignData = rawCampaign.data
	const generationError = campaignData?.generationError
	const sourceWebsite = campaignData?.sourceWebsite
	const isGenerated = campaignData?.isGenerated === true
	const hasContent = !!(campaignData?.title || campaignData?.requirements)

	// Only redirect for new campaigns where scraping was the first step and failed.
	// Existing campaigns being re-scraped already have content — keep them on the edit page.
	useEffect(() => {
		if (isGenerated && generationError && !hasContent) {
			void navigate({
				to: '/company/$companyId/campaign/new',
				params: { companyId: params.companyId },
				search: {
					url: sourceWebsite ?? undefined,
					scrapeError: generationError,
				},
			})
		}
	}, [isGenerated, generationError, hasContent, sourceWebsite, navigate, params.companyId])

	const { isGenerating: detailsIsGenerating } = useNewCampaignDetails(params.campaignId)
	useDirectImageTools({ campaignId: params.campaignId })

	const { isGenerating: feedAdIsGenerating } = useNewFeedEditor(params.campaignId)
	const { isGenerating: storyAdIsGenerating } = useNewStoryEditor(params.campaignId)
	const { isGenerating: landingPageIsGenerating } = useNewLandingPageEditor(params.campaignId)
	const { isGenerating: formIsGenerating } = useNewFormEditor(params.campaignId)

	const { store: thankYouStore } = useNewThankYouScreenEditor(params.campaignId)

	useNewCampaignReview(params.campaignId)
	useNewCampaignComplete(params.campaignId)
	useNewExportConfig({ campaignId: params.campaignId })
	useNewExportOptions()
	useNewEditorRegenerate(params.campaignId)

	useCampaignPoller({
		campaignId: params.campaignId,
		detailsIsGenerating,
		feedAdIsGenerating,
		storyAdIsGenerating,
		landingPageIsGenerating,
		formIsGenerating,
	})

	const handleRefresh = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: campaignsShowQueryKey(params.campaignId),
		})
	}, [params.campaignId])

	useEditorRefresh({ onRefresh: handleRefresh })

	const editorRef = useRef<EditorEditSimulatorsRef>(null)

	const { campaign } = useCampaignData({ campaignId: params.campaignId })

	return (
		<EditModeEditor
			ref={editorRef}
			isGenerated={!detailsIsGenerating}
			brandStore={brandStore}
			thankYouStore={thankYouStore}
			campaignId={params.campaignId}
			serviceFeePercent={campaign.serviceFeePercent}
		/>
	)
}
